import { StemType, TransportStates } from '@arpeggi/shared'
import { useEffect, useMemo, useState } from 'react'
import { IArpMedia } from '../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import ProfileRegistry, { IProfile } from '../../../stores/registry/ProfileRegistry'
import { useTreeState } from '../../../stores/Root'
import { getMultipleSampleAudioBuffers } from '../../../stores/song/sample/SampleUtils'
import { IDrumStemStore } from '../../../stores/song/stem/DrumStemStore'
import { IInstrumentStemStore } from '../../../stores/song/stem/InstrumentStemStore'
import { deserializeStemFromStorage } from '../../../stores/song/stem/StemUtils'
import { IStemStore } from '../../../stores/song/StemStore'
import { getStemColor } from '../../platform/color'
import { segmentStudioButton } from '../StudioUtils'

const useStemPreviewRow = (stem: IArpMedia, color: string = undefined) => {
  const [selectedLoopIndex, setSelectedLoopIndex] = useState<number>(0)
  const [artistProfile, setArtistProfile] = useState<IProfile>({})
  const { studio, alternatePlayer: studioStore } = useTreeState()
  const { song } = studio

  const samples = stem.subComponents
  if (!samples) return null

  let sampleName
  if (samples && samples.length > 1) {
    sampleName = 'Multiple'
  } else {
    sampleName = samples[0] ? ArpRegistry.retrieveFromCache(samples[0])?.metadata.title : ''
  }

  useEffect(() => {
    ProfileRegistry.getByIndexAsync(stem.user.walletAddress).then((profile) =>
      setArtistProfile(profile)
    )
  }, [stem.id])

  const rowColor =
    color || (ArpRegistry.readyForStudioUse && getStemColor(stem.getStemStore() as IStemStore))
  const isAdded = song.stems.findIndex((e) => e.remixedStemId === stem.id) > -1

  const handleAddButtonClick = () => {
    if (isAdded) {
      const stemIndex = song.stems.findIndex((e) => e.remixedStemId === stem.id)
      stemIndex !== -1 && song.removeStem(stemIndex)
      segmentStudioButton('Removed Stem', 'browse stems')
    } else {
      studio.addStemFromRemix(stem)
      segmentStudioButton('Added Stem', 'browse stems')
    }
  }

  const stemIndex = studioStore.song.stems.findIndex((e) => e.remixedStemId === stem.id)
  const isCurrentlyPlaying = stemIndex > -1
  const isPlaying = studioStore.transportState === TransportStates.PLAYING
  const handlePlayButton = () => {
    if (isPlaying) {
      if (isCurrentlyPlaying) {
        studioStore.stop()
        studioStore.clearSong()
      } else {
        studioStore.addStemFromRemix(stem)
      }
    } else {
      const stemStore: IStemStore = stem.getStemStore()
      const samples =
        stemStore.type() === StemType.DRUMS
          ? (stemStore as IDrumStemStore).drumSamples.map((e) => e.sampleIndex)
          : [(stemStore as IInstrumentStemStore).melodicSample.sampleIndex]
      ArpRegistry.getByIdsAsync(samples.map(String)).then((sampleArpMedia) =>
        getMultipleSampleAudioBuffers(sampleArpMedia).then(() => {
          studioStore.song.updateTempo(stem.metadata.bpm)
          studioStore.userSetPlayhead(0)
          studioStore.addStemFromRemix(stem)
          studioStore.play()
        })
      )
    }
  }

  const arp = useMemo(() => {
    return deserializeStemFromStorage(stem.metadata.arpeggiComposition)
  }, [stem.metadata.arpeggiComposition])

  return {
    artistProfile,
    samples,
    sampleName,
    handleAddButtonClick,
    isAdded,
    rowColor,
    arp,
    selectedLoopIndex,
    setSelectedLoopIndex
  }
}

export default useStemPreviewRow
